<template>
  <div class="onboarding">
    <OnboardingHeader />

    <div class="mw-600 px-5 ml-auto mr-auto">
      <h1 class="onboarding__title mb-15">
        {{ $t('onboarding.register.title') }}
      </h1>

      <v-form v-model="isValid" ref="registerForm" lazy-validation>
        <v-text-field
          v-model="name"
          :rules="[rules.required, rules.min]"
          :label="$t('onboarding.register.name')"
          autocomplete="one-time-code"
          outlined
        />

        <v-text-field
          v-model="email"
          :rules="[rules.minLength, rules.email]"
          :label="$t('onboarding.register.email')"
          autocomplete="one-time-code"
          outlined
        />

        <v-text-field
          :rules="[rules.phone]"
          v-model="phone"
          v-mask="
            phone && phone.length >= 15 ? mask.phone.phone9 : mask.phone.phone8
          "
          type="tel"
          :label="$t('onboarding.register.phone')"
          autocomplete="one-time-code"
          outlined
        />

        <v-text-field
          v-model="password"
          @input="passwordCheck"
          @click:append="showPass = !showPass"
          :append-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
          :type="showPass ? 'text' : 'password'"
          :rules="[rules.password]"
          :label="$t('onboarding.register.password')"
          autocomplete="one-time-code"
          outlined
        />

        <v-text-field
          v-model="passwordMatch"
          @input="passwordCheck"
          :type="showPass ? 'text' : 'password'"
          :error="!passwordMatches"
          :error-messages="!passwordMatches ? errorMessage : ''"
          :label="$t('onboarding.register.repeatPassword')"
          autocomplete="one-time-code"
          outlined
        />

        <div class="onboarding__options">
          <v-btn-toggle
            class="buttons"
            :class="{ hasError: accountTypeError }"
            v-model="accountType"
          >
            <v-btn class="buttons__item" @click="accountTypeError = false">
              <v-icon v-if="accountType === 0" color="primary" small left>
                mdi-check
              </v-icon>
              {{ $t('onboarding.register.accountType.business') }}
            </v-btn>
            <v-btn class="buttons__item" @click="accountTypeError = false">
              <v-icon v-if="accountType === 1" color="primary" small left>
                mdi-check
              </v-icon>
              {{ $t('onboarding.register.accountType.individual') }}
            </v-btn>
          </v-btn-toggle>
        </div>
        <small v-if="accountTypeError" class="pl-3 error--text">
          {{ $t('onboarding.register.accountType.error') }}
        </small>

        <div v-if="accountType === 0">
          <v-text-field
            v-model="customerName"
            :rules="[rules.required, rules.min]"
            :label="$t('onboarding.register.customerName')"
            autocomplete="one-time-code"
            outlined
            class="mt-8"
          />

          <HDocumentID
            v-model="cnpj"
            :default-value="cnpj"
            :rules="[rules.cnpj]"
          />
        </div>

        <HDocumentID
          v-if="accountType === 1"
          v-model="cpf"
          :default-value="cpf"
          :rules="[rules.cpf]"
        />

        <p class="text-center mt-5">
          <small>
            {{ $t('onboarding.register.creationAccept') }}
            <span
              @click="openModal('ONBOARDING_TERMS_MODAL')"
              class="primary--text text-decoration-underline"
            >
              {{ $t('onboarding.register.createTerms') }}
            </span>
          </small>
        </p>

        <v-row class="mt-8 mb-5" align="center">
          <v-col cols="12" sm="6">
            <v-btn
              href="/login"
              rel="noopener noreferrer"
              color="secondary"
              target="_blank"
              x-large
              block
              text
            >
              {{ $t('onboarding.register.alreadyHaveAccount') }}
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6" class="text-right">
            <v-btn
              :loading="isLoading"
              @click="validate"
              color="primary"
              elevation="0"
              x-large
              block
            >
              {{ $t('onboarding.register.createAccount') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </div>

    <div class="d-block text-center">
      <img class="onboarding__background-cities" :src="background" />
    </div>

    <ModalOnboardingTerms />
  </div>
</template>

<script>
import OnboardingHeader from '@/components/onboarding/OnboardingHeader'
import ModalOnboardingTerms from '@/components/onboarding/ModalOnboardingTerms'
import HDocumentID from '@/components/app/HDocumentID'

import { phone } from '@/util/masks'
import { required, password, email, minLength } from '@/util/errorHandling'
import { validateCPF, validateCNPJ } from '@/util/validate'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import background from '@/assets/images/bg-login-cities.jpg'

export default {
  name: 'Onboarding',

  components: {
    OnboardingHeader,
    ModalOnboardingTerms,
  },

  data() {
    return {
      isValid: true,
      showPass: false,
      name: '',
      email: '',
      phone: '',
      cpf: '',
      cnpj: '',
      password: '',
      passwordMatch: '',
      customerName: '',
      accountType: null,
      regexEmail: /.+@.+\..+/,
      accountTypeError: false,
      passwordMatches: true,
      mask: { phone },
      rules: {
        required,
        password,
        email,
        minLength,
        phone: (val) =>
          val.length >= 14 || this.$t('onboarding.register.errors.phone'),
        mail: (val) =>
          this.regexEmail.test(val) ||
          this.$t('onboarding.register.errors.mail'),
        min: (val) =>
          val.length >= 7 || this.$t('onboarding.register.errors.minimum'),
        cpf: (val) =>
          validateCPF(val) || this.$t('onboarding.register.errors.cpf'),
        cnpj: (val) =>
          validateCNPJ(val) || this.$t('onboarding.register.errors.cnpj'),
      },

      background,
    }
  },

  computed: {
    ...mapGetters(['isLoading']),

    errorMessage() {
      return this.$t('onboarding.register.errors.passwordMismatch')
    }
  },

  methods: {
    ...mapMutations(['openModal']),
    ...mapActions(['createProfile']),

    handleCleanTokens() {
      localStorage.clear()
    },

    validate() {
      this.$refs.registerForm.validate()
      if (this.accountType !== 0 && this.accountType !== 1) {
        this.isValid = false
        this.accountTypeError = true
      }

      if (this.isValid && this.password === this.passwordMatch) {
        const newUser = {
          name: this.name,
          cnpj:
            this.accountType === 0
              ? this.cnpj.replace(/\D/g, '')
              : this.cpf.replace(/\D/g, ''),
          phone: this.phone.replace(/\D/g, ''),
          email: this.email,
          customerCreatProfileRequest: {
            name:
              this.accountType === 0
                ? this.customerName
                : this.name,
            cpfcnpj:
              this.accountType === 0
                ? this.cnpj.replace(/\D/g, '')
                : this.cpf.replace(/\D/g, ''),
            phone: this.phone.replace(/\D/g, ''),
            email: this.email,
            password: this.password,
          },
        }

        this.createProfile(newUser)
      }
    },

    passwordCheck() {
      if (!this.passwordMatch) return

      this.password == this.passwordMatch
        ? this.passwordMatches = true
        : this.passwordMatches = false
    },
  },

  mounted() {
    this.handleCleanTokens()
  },
}
</script>

<style lang="scss" scoped>
.buttons {
  width: 100%;

  .v-btn {
    position: relative;

    &:nth-child(1) {
      margin-right: -1px;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    &:nth-child(2) {
      margin-left: -1px;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }

  &.hasError {
    box-shadow: 0 0 0 2px red;

    .v-btn {
      color: red;
    }
  }
}
</style>
